import {
  SharedTableBodyAttributes,
  SharedTableBodyTabs,
} from "@/components/ProgressList/results/SharedTableBody";
import {
  SharedTableHeadAttributes,
  SharedTableHeadTabNames,
} from "@/components/ProgressList/results/SharedTableHead";
import { Attribute } from "@/components/ProgressList/types";
import { useProgressListSearchResults } from "@/components/ProgressList/useProgressListSearchResults";
import { SortIndicator } from "@/components/SortIndicator";
import * as Config from "@/config";
import { useProgressStore } from "@/stores/progress";

export const ServiceStartInfoTableHead = () => {
  const [serviceStartInfoType, listAttributes] = useProgressStore((state) => [
    state.serviceStartInfoType,
    state.listAttributes,
  ]);
  const isProgress =
    serviceStartInfoType === Config.ServiceStartInformationList.progress;
  const isCollect =
    serviceStartInfoType === Config.ServiceStartInformationList.collect;
  const { splitServiceStartInfoListAttributes } =
    useProgressListSearchResults();
  const {
    collectList,
    collectListBasicInfo,
    collectListServiceInfo,
    collectListDrawing,
    collectListReference,
  } = splitServiceStartInfoListAttributes(listAttributes);

  return (
    <>
      {isProgress && <SharedTableHeadTabNames />}
      {isProgress && <RequestedLocationHeader />}
      <SharedTableHeadAttributes
        CollectList={isCollect ? collectList : undefined}
      />
      {isCollect && (
        <>
          <ServiceStartInfoHeaderName
            headerName={Config.ServiceStartInfoCollectHeaderName.basicInfo}
            colSpan={collectListBasicInfo.length}
          />
          <ServiceStartInfoHeaderName
            headerName={Config.ServiceStartInfoCollectHeaderName.serviceInfo}
            colSpan={collectListServiceInfo.length}
          />
          <ServiceStartInfoHeaderName
            headerName={Config.ServiceStartInfoCollectHeaderName.drawing}
            colSpan={collectListDrawing.length}
          />
          <ServiceStartInfoHeaderName
            headerName={Config.ServiceStartInfoCollectHeaderName.reference}
            colSpan={collectListReference.length}
          />
        </>
      )}
    </>
  );
};

interface ServiceStartInfoTableBodyProps {
  data: any;
  checked: boolean;
  openMapModal: (id: number) => void;
}

export const ServiceStartInfoTableBody = (
  props: ServiceStartInfoTableBodyProps
) => {
  const [serviceStartInfoType, listAttributes] = useProgressStore((state) => [
    state.serviceStartInfoType,
    state.listAttributes,
  ]);
  const isProgress =
    serviceStartInfoType === Config.ServiceStartInformationList.progress;
  const isCollect =
    serviceStartInfoType === Config.ServiceStartInformationList.collect;
  const { splitServiceStartInfoListAttributes } =
    useProgressListSearchResults();
  const {
    collectList,
    collectListBasicInfo,
    collectListServiceInfo,
    collectListDrawing,
    collectListReference,
  } = splitServiceStartInfoListAttributes(listAttributes);
  return (
    <>
      {isProgress && <SharedTableBodyTabs data={props.data} />}
      {isProgress && <RequestedLocationBody {...props} />}
      <SharedTableBodyAttributes
        {...props}
        CollectList={isCollect ? collectList : undefined}
      />
      {isCollect && (
        <>
          <SharedTableBodyAttributes
            {...props}
            CollectList={collectListBasicInfo}
          />
          <SharedTableBodyAttributes
            {...props}
            CollectList={collectListServiceInfo}
          />
          <SharedTableBodyAttributes
            {...props}
            CollectList={collectListDrawing}
          />
          <SharedTableBodyAttributes
            {...props}
            CollectList={collectListReference}
          />
        </>
      )}
    </>
  );
};

export const ServiceStartInfoTableHeadAttributes = () => {
  const listAttributes = useProgressStore((state) => state.listAttributes);
  const { splitServiceStartInfoListAttributes } =
    useProgressListSearchResults();
  const {
    collectListBasicInfo,
    collectListServiceInfo,
    collectListDrawing,
    collectListReference,
  } = splitServiceStartInfoListAttributes(listAttributes);

  return (
    <>
      <ServiceStartInfoTableHeadAttribute
        attributes={collectListBasicInfo}
        headerName={Config.ServiceStartInfoCollectHeaderName.basicInfo}
      />
      <ServiceStartInfoTableHeadAttribute
        attributes={collectListServiceInfo}
        headerName={Config.ServiceStartInfoCollectHeaderName.serviceInfo}
      />
      <ServiceStartInfoTableHeadAttribute
        attributes={collectListDrawing}
        headerName={Config.ServiceStartInfoCollectHeaderName.drawing}
      />
      <ServiceStartInfoTableHeadAttribute
        attributes={collectListReference}
        headerName={Config.ServiceStartInfoCollectHeaderName.reference}
      />
    </>
  );
};

interface ServiceStartInfoTableHeadAttributeProps {
  attributes: Attribute[];
  headerName: string;
}

export const ServiceStartInfoTableHeadAttribute = (
  props: ServiceStartInfoTableHeadAttributeProps
) => {
  return (
    <>
      {props.attributes.map((attr: Attribute) => {
        const listWidth = attr?.attribute_setting_values.find(
          (setting_value: any) =>
            setting_value.attribute_setting_code ===
            Config.ATTRIBUTE_SETTING_LIST_WIDTH
        );
        const width = listWidth ? `${listWidth.value}px` : "200px";
        return (
          <th
            key={`service-start-info-attr-head-${attr.id}`}
            className="uk-padding-remove-horizontal"
            style={{ minWidth: width, maxWidth: width }}
          >
            <div className="border-box" />
            <div className="status-box">
              <div className="line-break">
                <span>{attr.name}</span>
              </div>
            </div>
            <SortIndicator
              item_name={Config.ProgressListSortItem.ATTRIBUTES}
              column_name={attr.column_name}
            />
          </th>
        );
      })}
    </>
  );
};

export const ServiceStartInfoHeaderName = (props: {
  colSpan: number;
  headerName: string;
}) => {
  return (
    <th colSpan={props.colSpan} className="uk-padding-remove-horizontal">
      <div className="border-box" />
      <div className="uk-text-center">{props.headerName}</div>
    </th>
  );
};

export const ServiceStartInfoChangeListButtons = () => {
  const [setServiceStartInfoType, serviceStartInfoType] = useProgressStore(
    (state) => [state.setServiceStartInfoType, state.serviceStartInfoType]
  );
  const selectedListClass = (type: Config.ServiceStartInformationList) => {
    return serviceStartInfoType == type
      ? "uk-button-refer"
      : "uk-button-cancel";
  };

  const changeServiceStartInfoList = (
    type: Config.ServiceStartInformationList
  ) => {
    setServiceStartInfoType(type);
  };

  return (
    <>
      <button
        className={`uk-button--l uk-margin-left ${selectedListClass(
          Config.ServiceStartInformationList.progress
        )}`}
        onClick={() =>
          changeServiceStartInfoList(
            Config.ServiceStartInformationList.progress
          )
        }
      >
        進捗一覧
      </button>
      <button
        className={`uk-button--w uk-margin-left ${selectedListClass(
          Config.ServiceStartInformationList.manage
        )}`}
        onClick={() =>
          changeServiceStartInfoList(Config.ServiceStartInformationList.manage)
        }
      >
        提供用予定管理リスト
      </button>
      <button
        className={`uk-button--l uk-margin-left ${selectedListClass(
          Config.ServiceStartInformationList.collect
        )}`}
        onClick={() =>
          changeServiceStartInfoList(Config.ServiceStartInformationList.collect)
        }
      >
        資料収集リスト
      </button>
    </>
  );
};

const RequestedLocationHeader = () => {
  return (
    <>
      <th
        key="requested-location-header"
        rowSpan={2}
        style={{ minWidth: 80, maxWidth: 80 }}
        className="sort-indicator-wrap"
      >
        <div className="border-box" />
        要望地点
        <br />
        入力情報
        <SortIndicator
          item_name={Config.ProgressListSortItem.ATTRIBUTES}
          column_name={Config.ATTRIBUTE_COLUMN_NAME.requestedLocation}
        />
      </th>
    </>
  );
};

interface RequestedLocationBodyProps {
  data: any;
  checked: boolean;
  openMapModal: (id: number) => void;
}

const RequestedLocationBody = ({
  data,
  checked,
  openMapModal,
}: RequestedLocationBodyProps) => {
  // data_attribute_text_values が存在し、少なくとも1つの要素があり、その要素に text プロパティがある
  const textValue = data?.data_attribute_text_values?.[0]?.text;

  // textValue が存在する場合、それを JSON.parse() でパースし、その結果に featureKey というキーが存在する
  const hasFeatureKey = (() => {
    // 配列が存在しない場合は false
    if (!data?.data_attribute_text_values) {
      return false;
    }

    // some() で配列のいずれかの要素が条件を満たすかチェック
    return data.data_attribute_text_values.some(item => {
      const textValue = item?.text;
      if (!textValue) {
        return false;
      }
      try {
        const parsedJson = JSON.parse(textValue);
        // 'featureKey' が含まれていれば true を返す
        return typeof parsedJson === 'object' && parsedJson !== null && 'featureKey' in parsedJson;
      } catch (e) {
        // パースエラーの場合は false
        return false;
      }
    });
  })();

  const handleClick = () => {
    if (hasFeatureKey) {
      openMapModal(data.id);
    }
  };

  return (
    <>
      <td
        key="requested-location-body"
        className="uk-text-center"
        style={{
          minWidth: 80,
          maxWidth: 80,
          cursor: hasFeatureKey ? "pointer" : "default",
        }}
        data-tooltip-id="progress-tooltip"
        onClick={handleClick}
      >
        <div className="border-box" />
        {hasFeatureKey && "〇"}
      </td>
    </>
  );
};
